import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';
import * as angular from 'angular';
import { downgradeComponent, downgradeInjectable, UpgradeModule } from '@angular/upgrade/static';
import AppModule from '../ng2/app.module';

import { AppointmentRequestsComponent } from '../ng2/home/appointment-requests/appointment-requests.component.ts';
import { CancelledAppointmentRequestsComponent } from '../ng2/home/appointment-requests/cancelled-appointment-requests/cancelled-appointment-requests.component.ts';
import { AppointmentRequestCancelComponent } from '../ng2/home/appointment-requests/appointment-request-cancel.component.ts';
import { RecallListComponent } from '../ng2/home/recall-list/recall-list.component.ts';
import { ReasonsNavigationComponent } from '../ng2/main/reasons/reasons-navigation/reasons-navigation.component.ts';
import { DatePickerComponent } from '../ng2/shared/datepicker/datepicker.component.ts';
import { ReasonFormComponent } from '../ng2/main/reasons/reason-form/reason-form.component.ts';
import { LinkedPatientsComponent } from '../ng2/main/reasons/linked-patients/linked-patients.component.ts';
import { TopNavigationComponent } from '../ng2/main/top-navigation/top-navigation.component.ts';
import { AppointmentMomentPreferencesComponent } from '../ng2/shared/appointment-moment-preferences/appointment-moment-preferences.component.ts';
import { FlashComponent } from '../ng2/shared/flash/flash.component.ts';

import { FeedbackMessageComponent } from '../ng2/shared/feedback-message/feedback-message.component.ts';
import { LayoutBoxComponent } from '../ng2/shared/layout-box/layout-box.component.ts';
import { AppInputComponent } from '../ng2/shared/app-input/app-input.component.ts';
import { AppButtonComponent } from '../ng2/shared/app-button/app-button.component.ts';
import { AppHeaderComponent } from '../ng2/shared/app-header/app-header.component.ts';
import { AppFooterComponent } from '../ng2/shared/app-footer/app-footer.component.ts';
import { PatientNameComponent } from '../ng2/shared/patient-name/patient-name.component.ts';
import { AppointmentsConfirmationComponent } from '../ng2/main/appointments/appointments-confirmation/appointments-confirmation.component';

import { NgReduxStub } from '../ng2/shared/services/ng-redux-stub.service.ts';

import { AppointmentsService } from '../ng2/shared/services/appointments.service.ts';
import { ReasonsService } from '../ng2/shared/services/reasons.service.ts';

const vendors = [
  'ngAnimate',
  'ngCookies',
  'ngSanitize',
  'ui.router',
  'ngMessages',
  'pascalprecht.translate',
  'ngResource',
  'ngSanitize',
  'ng-sortable',
];

const modules = [
  'hospitalBooking',
  'profile',
  'kiosk',
  'signin',
];

if (application.use.mock) {
  vendors.push('ngMockE2E');
}

// Use the original ngRedux vendor lib instead of the ng2 stub in test environment
// because downgraded services don't play well with Jasmine specs
if (window.jasmine) {
  vendors.push('ngRedux');
}

if (application.use.sentryUrl) {
  vendors.push('ngSentry');
}

const main = angular.module('main', vendors.concat(modules));

const autoload = require.context('.', true, /\.(html|json|woff|woff2|ttf|eot|svg|gif|png)$/);
const object = autoload.keys();
object.forEach((name) => {
  autoload(name);
});

if (application.use.mock) {
  main
    .service('SubscriptionDataMock', require('./mocks/subscription-data-mock.js'))
    .service('SubscriptionMock', require('./mocks/subscription-mock.js'))
    .service('AuthenticationMock', require('./mocks/authentication-mock.js'))
    .service('GroupMock', require('./mocks/group-mock.js'))
    .service('PatientMock', require('./mocks/patient-mock.js'))
    .service('AppointmentMock', require('./mocks/appointment-mock.js'))
    .service('ReasonMock', require('./mocks/reason-mock.js'))
    .service('OfferingMock', require('./mocks/offering-mock.js'))
    .service('ProfileMock', require('./mocks/profile-mock.js'))
    .run([
      'AppointmentMock',
      'AuthenticationMock',
      'GroupMock',
      'PatientMock',
      'ProfileMock',
      'ReasonMock',
      'OfferingMock',
      'SubscriptionDataMock',
      'SubscriptionMock',
      () => ({}),
    ]);
}
/* eslint-enable */

main
  .provider('$local', require('./providers/local-storage.js'))
  .provider('$session', require('./providers/session-storage.js'))
  .config(require('./config/initializers/location.js'))
  .config(require('./config/initializers/lodash.js'))
  .config(require('./config/initializers/translate.js'))
  .config(require('./config/routes.js'))
  .config(require('./config/initializers/interceptors.js'))
  .run(require('./config/listeners/session.js'))
  .run(require('./config/listeners/security.js'))
  .service('AccountService', require('./services/account-service.js'))
  .service('LanguageService', require('./services/language-service.js'))
  .service('LoadingService', require('./services/loading-service.js'))
  .service('PatientAppContextService', require('./services/patient-app-context-service.js'))
  .service('PatientsService', require('./services/patients-service.js'))
  .service('ReasonService', require('./services/reason-service.js'))
  .service('ProductService', require('./services/product-service.js'))
  .service('SecurityService', require('./services/security-service.js'))
  .service('SessionService', require('./services/session-service.js'))
  .service('ValidationService', require('./services/validation-service.js'))
  .factory('Appointments', require('./factories/appointment-resource.js'))
  .factory('Availabilities', require('./factories/availability-resource.js'))
  .factory('AppointmentReasons', require('./factories/appointment-reason-resource.js'))
  .factory('Forms', require('./factories/form-resource.js'))
  .factory('Groups', require('./factories/group-resource.js'))
  .factory('LinkedPatients', require('./factories/linked-patient-resource.js'))
  .factory('Offerings', require('./factories/offering-resource.js'))
  .factory('Patients', require('./factories/patient-resource.js'))
  .factory('Session', require('./factories/session-resource.js'))
  .factory('Subscriptions', require('./factories/subscription-resource.js'))
  .factory('ReasonsService', downgradeInjectable(ReasonsService))
  .factory('AppointmentsService', downgradeInjectable(AppointmentsService))
  .component('appointmentAdditionalInfo', require(('./components/appointment-additional-info/appointment-additional-info.js')))
  .component('appointmentAttachments', require(('./components/appointment-attachments/appointment-attachments.js')))
  .component('appointmentCancel', require(('./components/appointment-cancel.js')))
  .component('appointmentDetails', require(('./components/appointment-details/appointment-details.js')))
  .component('availabilities', require(('./components/availabilities/availabilities.js')))
  .component('patientSelector', require(('./components/patient-selector/patient-selector.js')))
  .component('accountMenu', require(('./components/account-menu.js')))
  .component('loading', require(('./components/loading.js')))
  .component('groupInfo', require(('./components/group-info/group-info.js')))
  .component('patientHeader', require(('./components/patient-header.js')))
  .component('upcomingAppointments', require(('./components/upcoming-appointments/upcoming-appointments.js')))
  .directive('appointmentsConfirmation', downgradeComponent({
    component: AppointmentsConfirmationComponent,
    inputs: [],
    outputs: [],
  }))
  .directive('appointmentRequests', downgradeComponent({
    component: AppointmentRequestsComponent,
    inputs: [],
    outputs: [],
  }))
  .directive('cancelledAppointmentRequests', downgradeComponent({
    component: CancelledAppointmentRequestsComponent,
    inputs: [],
    outputs: [],
  }))
  .directive('appointmentRequestCancel', downgradeComponent({
    component: AppointmentRequestCancelComponent,
    inputs: [],
    outputs: [],
  }))
  .directive('recallList', downgradeComponent({
    component: RecallListComponent,
    inputs: [],
    outputs: [],
  }))
  .directive('layoutBox', downgradeComponent({
    component: LayoutBoxComponent,
    inputs: [],
    outputs: [],
  }))
  .directive('appHeader', downgradeComponent({
    component: AppHeaderComponent,
    inputs: [],
    outputs: [],
  }))
  .directive('appFooter', downgradeComponent({
    component: AppFooterComponent,
    inputs: [],
    outputs: [],
  }))
  .directive('appInput', downgradeComponent({
    component: AppInputComponent,
    inputs: ['label', 'control', 'type'],
    outputs: [],
  }))
  .directive('appButton', downgradeComponent({
    component: AppButtonComponent,
    inputs: ['label', 'control'],
    outputs: ['onClick'],
  }))
  .directive('feedbackMessage', downgradeComponent({
    component: FeedbackMessageComponent,
    inputs: ['title', 'text', 'closable'],
    outputs: ['dismiss'],
  }))
  .component('historicalAppointments', require(('./components/historical-appointments.js')))
  .component('yesNoDialog', require(('./components/yes-no-dialog.js')))
  .component('main', require(('./components/main.js')))
  .component('patientFooter', require(('./components/patient-footer.js')))
  .component('patientLegal', require(('./components/patient-legal.js')))
  .component('reasons', require('./components/reasons/reasons.js'))
  .component('reasonPreferences', require('./components/reasons/reason-preferences.js'))
  .component('reasonVisitChoices', require('./components/reasons/reason-visit-choices.js'))
  .component('reasonSelectHcp', require('./components/reasons/reason-select-hcp.js'))
  .directive('dateField', require('./directives/date-field.js'))
  .directive('compareFields', require('./directives/compare-fields.js'))
  .directive('hinValidation', require('./directives/hin-validation'))
  .directive('passwordRequirements', require('./directives/password-requirements.js'))
  .directive('product', require('./directives/product.js'))
  .directive('textTruncate', require('./directives/text-truncate.js'))
  .directive('multilineEllipsis', require('./directives/multiline-ellipsis.js'))
  .filter('bytesSize', require('./filters/bytes-size-filter.js'))
  .filter('apiEndpoint', require('./filters/api-endpoint-filter.js'))
  .filter('capitalize', require('./filters/capitalize-filter.js'))
  .filter('datetime', require('./filters/datetime-filter.js'))
  .filter('displayDate', require('./filters/display-date-filter.js'))
  .filter('displayName', require('./filters/display-name-filter.js'))
  .filter('timezoneFormat', require('./filters/timezone-format-filter.js'))
  .filter('map', require('./filters/map-filter.js'))
  .filter('noNumberWordBreak', require('./filters/no-number-word-break-filter.js'))
  .filter('kebabCase', require('./filters/kebab-case-filter.js'))
  .filter('patientName', require('./filters/patient-name-filter.js'))
  .filter('timerFormat', require('./filters/timer-format-filter.js'))
  .directive('reasonForm', downgradeComponent({
    component: ReasonFormComponent,
    inputs: ['reasonFormPromise'],
    outputs: [],
  }))
  .directive('reasonsNavigation', downgradeComponent({
    component: ReasonsNavigationComponent,
    inputs: ['reason', 'noForm'],
    outputs: [],
  }))
  .directive('topNavigation', downgradeComponent({
    component: TopNavigationComponent,
    inputs: ['breadcrumbSteps'],
    outputs: [],
  }))
  .directive('linkedPatients', downgradeComponent({
    component: LinkedPatientsComponent,
    inputs: [],
    outputs: [],
  }))
  .directive('patientName', downgradeComponent({
    component: PatientNameComponent,
    inputs: ['name', 'oldUi'],
    outputs: [],
  }))
  .directive('datepicker', downgradeComponent({
    component: DatePickerComponent,
    inputs: ['calendarOptions', 'date', 'max', 'min', 'futureDate', 'placeholder', 'showIcon', 'inputName', 'disabled', 'clearable', 'actAsInput', 'dateFormat'],
    outputs: ['dateChange'],
  }))
  .directive('flash', downgradeComponent({ component: FlashComponent }))
  .directive('appointmentMomentPreferences', downgradeComponent({
    component: AppointmentMomentPreferencesComponent,
    inputs: ['titleId', 'preferences', 'soonestPossibleDate', 'allowConsecutiveAppointments'],
    outputs: [],
  }));

if (!window.jasmine) {
  platformBrowserDynamic().bootstrapModule(AppModule).then((platformRef) => {
    const upgrade = platformRef.injector.get(UpgradeModule);
    upgrade.bootstrap(document.body, ['main'], { strictDi: true });
  });
  // NgReduxStub is used only in dev/production environment, not in test
  main.factory('$ngRedux', downgradeInjectable(NgReduxStub));
} else {
  // In test environment, use old states
  main.config(require('./config/states.js'));
}

module.exports = main;



// WEBPACK FOOTER //
// ./main/main.js